import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import {
  putManySchedules,
  removeSchedule,
  putSchedule,
  invalidateCache,
  isCacheStale,
  clearSchedules,
} from '../../components/Schedule/schedulesCache.js';

import {
  getLatestControllerData, // getLastestData
  applySetPointsControllerValues,
  checkManualControlPasswordApi,
  getControllerRangeData,
  getControllerData,
  getDeviceInfo,
} from 'services/dataService';
import {
  getUserSchedules,
  saveScheduleApi,
  saveRecommendationsForDevicesAPI,
  saveRecommendationScheduleApi,
  getSchedule,
  activateScheduleForDevicesApi,
  activateScheduleForSingleDeviceApi,
  activateScheduleById,
  activateRecommendationsApi,
  getRecommendationApi,
  deleteSchedule,
  getUserSchedulesDetails,
  getControllerSchedules,
} from 'services/scheduleService';
import { filterDevices } from 'helpers/filterHelpers';
import { getAllControllers } from 'services/devicesService';

export const fetchControllers = createAsyncThunk('controllers/fetchIDs', async () => {
  const res = await getAllControllers();

  return res;
});

export const fetchInitialData = createAsyncThunk('controllers/fetchData', async params => {
  const res = await getControllerData(params.measurements, params.controller_id, params.timeRange);

  return { id: params.controller_id, data: res };
});

export const fetchCurrentData = createAsyncThunk('controllers/fetchCurrentData', async params => {
  const res = await getLatestControllerData(params.fields, params.controller_id);

  return { id: params.controller_id, data: res };
});

// fetch data summary for graphs
export const fetchControllerRangeData = createAsyncThunk('controllers/fetchRangeData', async (params, { rejectWithValue }) => {
  try {
    const res = await getControllerRangeData(params.measurements, params.controller_id, params.timeRange, params.userTimeZone);
    if (!res.measurements) {
      params.navigate('/controllers-list/');
      return;
    }
    return { id: params.controller_id, data: res };
  } catch (error) {
    return rejectWithValue({ message: error.message, statusCode: error.statusCode });
  }
});

// fetch controller info for edit device form
export const fetchDeviceInfo = createAsyncThunk('controllers/fetchDeviceInfo', async (params, { rejectWithValue }) => {
  try {
    const res = await getDeviceInfo(params.deviceId);
    return res;
  } catch (error) {
    // Keep the HTTP status code so DeviceForm can redirect 403 errors to /unauthorized.
    return rejectWithValue({ message: error.message, statusCode: error.statusCode });
  }
});

export const checkManualControlPassword = createAsyncThunk('controllers/checkManualPassword', async password => {
  const res = await checkManualControlPasswordApi(password);
  return res;
});

// export const setControllerSetPoints = createAsyncThunk('controller/setRealTimeValues', async params => {
//   const res = await setRealTimeControllerValues(params.controller_id, params.data);

//   return res;
// });

export const setControllerSetPoints = createAsyncThunk(
  'controller/setRealTimeValues',
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      const res = await applySetPointsControllerValues(
        // state.controllers.token,
        params.controller_id,
        params.device_mac,
        params.data,
      );

      return res;
    } catch (err) {
      return rejectWithValue({
        status: err.status || 0,
        message: err.message || 'Unknown error',
        payload: err.payload || null,
      });
    }
  },
);

/////////////// SCHEDULES THUNKS ///////////////

// FETCH all schedules of the user
export const fetchUserSchedules = createAsyncThunk('controllers/fetchUserSchedules', async (_, { rejectWithValue }) => {
  try {
    const schedules = await getUserSchedules();
    // Update cache with fresh data and set refresh timestamp
    // putManySchedules(schedules);
    return schedules; // Redux entityAdapter handles this in fulfilled case
  } catch (error) {
    console.error('Error fetching schedules:', error);
    return rejectWithValue({ message: error?.message || 'Failed to fetch schedules' });
  }
});

export const fetchSchedulesDetails = createAsyncThunk('controllers/fetchSchedulesDetails', async (_, { rejectWithValue }) => {
  try {
    const schedules = await getUserSchedulesDetails();
    return schedules;
  } catch (error) {
    console.error('Error fetching schedules:', error);
    return rejectWithValue({ message: error?.message || 'Failed to fetch schedules' });
  }
});

// FETCH all schedules of a controller
export const fetchControllerSchedules = createAsyncThunk(
  'controllers/fetchControllerSchedules',
  async (params, { rejectWithValue }) => {
    try {
      const res = await getControllerSchedules(params.deviceId, params.includePatterns);
      // Update cache with fresh data and set refresh timestamp
      // putManySchedules(schedules);
      return res; // Redux entityAdapter handles this in fulfilled case
    } catch (error) {
      console.error('Error fetching schedules:', error);
      return rejectWithValue({ message: error?.message || 'Failed to fetch schedules' });
    }
  },
);

// export const getSchedules = createAsyncThunk('schedule/getSchedules', async ({ getState, dispatch }) => {
//   const state = getState();
//   const existingSchedules = state.schedules;

//   // If we already have users data, return it
//   if (existingSchedules && existingSchedules.length > 0) {
//     return existingSchedules;
//   }

//   // If no data exists, fetch it
//   await dispatch(fetchSchedulesDetails());

//   // Return the newly fetched data
//   const updatedState = getState();
//   return updatedState.schedules;
// });

// check staleness and refresh if needed
// Call this before critical operations
export const ensureFreshSchedules = createAsyncThunk('controllers/ensureFreshSchedules', async (_, { dispatch, getState }) => {
  const state = getState();
  const schedulesStatus = state.controllers.schedulesStatus;

  // If we've never loaded schedules, or cache is stale, refresh
  if (schedulesStatus === 'idle' || isCacheStale()) {
    await dispatch(fetchUserSchedules()).unwrap();
    return { refreshed: true };
  }

  return { refreshed: false, reason: 'cache still fresh' };
});

// FETCH a single schedule by its ID
export const fetchSchedule = createAsyncThunk('controllers/fetchSchedule', async (scheduleId, { rejectWithValue }) => {
  try {
    const schedule = await getSchedule(scheduleId);

    // Update cache with this schedule
    putSchedule(schedule);

    return schedule;
  } catch (error) {
    console.error('Error fetching schedule:', error);
    return rejectWithValue({ message: error?.message || 'Fetch failed' });
  }
});

// DELETE: Remove from both Redux and cache
export const deleteScheduleById = createAsyncThunk('controllers/deleteSchedule', async (scheduleId, { rejectWithValue }) => {
  try {
    const result = await deleteSchedule(scheduleId);

    // Immediately remove from cache
    removeSchedule(scheduleId);

    return { scheduleId, result };
  } catch (error) {
    console.error('Error deleting schedule:', error);
    return rejectWithValue({ message: error?.message || 'Delete failed' });
  }
});

// SAVE: Add to both Redux and cache
export const saveSchedule = createAsyncThunk('controllers/saveSchedule', async (params, { rejectWithValue }) => {
  try {
    const result = await saveScheduleApi(params.scheduleData, params.patterns, params.deviceIds);
    return result;
  } catch (error) {
    console.error('Error saving schedule:', error);
    return rejectWithValue(error?.message || error?.toString() || 'Save failed');
  }
});

// ACTIVATE: Invalidate cache since status changed
export const activateScheduleForDevices = createAsyncThunk(
  'controllers/activateScheduleForDevices',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      // Ensure fresh data before activation - not from cache
      // await dispatch(ensureFreshSchedules()).unwrap();

      const res = await activateScheduleForDevicesApi(params.scheduleId, params.devicesIds);

      // cleat cache and refetch
      invalidateCache(); // all cache expired
      // await dispatch(fetchUserSchedules()).unwrap(); // fetch and re save in cache
      // await dispatch(fetchSchedulesDetails()).unwrap();

      return res;
    } catch (e) {
      return rejectWithValue(e?.message || 'Request failed');
    }
  },
);

// ACTIVATE: Invalidate cache since status changed
export const activateScheduleForSingleDevice = createAsyncThunk(
  'controllers/activateScheduleForSingleDevice',
  async (params, { rejectWithValue }) => {
    try {
      // Ensure fresh data before activation - not from cache
      // await dispatch(ensureFreshSchedules()).unwrap();

      const res = await activateScheduleForSingleDeviceApi(params.scheduleId, params.deviceId);

      // cleat cache and refetch
      invalidateCache(); // all cache expired

      return res;
    } catch (e) {
      return rejectWithValue(e?.message || 'Request failed');
    }
  },
);

// ACTIVATE: Invalidate cache since status changed
// export const activateScheduleByScheduleId = createAsyncThunk(
//   'controllers/activateScheduleById',
//   async (params, { dispatch, rejectWithValue }) => {
//     try {

//       const res = await activateScheduleById(params.scheduleId);

//       // clear cache and refetch
//       invalidateCache(); // all cache expired

//       return res;
//     } catch (e) {
//       return rejectWithValue(e?.message || 'Request failed');
//     }
//   },
// );

// ACTIVATE: Recommendations for device_ids
export const activateRecommendationsForDevices = createAsyncThunk(
  'controllers/activateRecommendationsForDevices',
  async (device_ids, { dispatch, rejectWithValue }) => {
    if (!device_ids || device_ids.length === 0) {
      return rejectWithValue('No device IDs provided');
    }

    try {
      const res = await activateRecommendationsApi(device_ids);

      // Clear cache and refetch like other activation functions
      invalidateCache();
      await dispatch(fetchUserSchedules()).unwrap();
      await dispatch(fetchSchedulesDetails()).unwrap();

      return res;
    } catch (e) {
      return rejectWithValue(e?.message || 'Request failed');
    }
  },
);

/**
 * Logout - clear all cache
 */
export const logoutClearCache = createAsyncThunk('controllers/logoutClearCache', async () => {
  clearSchedules();
  return { cleared: true };
});

export const saveAndActivateSchedule = createAsyncThunk(
  'controllers/saveAndActivateSchedule',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      // Step 1: Save the schedule
      const saveResult = await dispatch(
        saveSchedule({
          scheduleData: params.scheduleData,
          patterns: params.patterns,
          deviceIds: params.deviceIds,
        }),
      ).unwrap();

      // Step 2: Activate it (this will handle fetching fresh data)
      const activateResult = await activateScheduleForSingleDeviceApi(String(saveResult.id), String(params.deviceIds[0]));
      return { saveResult, activateResult };
    } catch (error) {
      console.error('Error in save and activate:', error);
      return rejectWithValue({ message: error?.message || 'Operation failed' });
    }
  },
);

/**
 * Update schedule patterns
 * Invalidates cache since patterns are stored there
 */
export const updateSchedulePatterns = createAsyncThunk(
  'controllers/updateSchedulePatterns',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      // Make API call to update patterns
      const result = await updateSchedulePatternsApi(params.scheduleId, params.patterns);

      // Invalidate cache and refetch to get updated patterns
      invalidateCache();
      await dispatch(fetchSchedule(params.scheduleId)).unwrap();

      return result;
    } catch (error) {
      console.error('Error updating patterns:', error);
      return rejectWithValue({ message: error?.message || 'Update failed' });
    }
  },
);

// SAVE for deviceID auto-generate Recommendation
export const saveRecommendationSchedule = createAsyncThunk('controllers/saveSchedule', async (params, { rejectWithValue }) => {
  try {
    const res = await saveRecommendationScheduleApi(params.deviceId, params.scheduleName);
    // Add to cache if it includes patterns
    if (res.schedule) {
      putSchedule(res.schedule);
    }
    return res;
  } catch (e) {
    return rejectWithValue(e.message || 'Request failed');
  }
});

// SAVE for deviceIDs auto-generate Recommendations
export const saveRecommendationForDevices = createAsyncThunk(
  'controllers/recommendation/save',
  async (params, { rejectWithValue }) => {
    try {
      const res = await saveRecommendationsForDevicesAPI(params.deviceIds);
      // Add to cache if it includes patterns
      if (res.schedules) {
        putManySchedules(res.schedules);
      }
      return res;
    } catch (e) {
      return rejectWithValue(e.message || 'Request failed');
    }
  },
);

export const getRecommendation = createAsyncThunk('controllers/getRecommendation', async (deviceId, { rejectWithValue }) => {
  try {
    const res = await getRecommendationApi(deviceId);
    res.id = 0;
    putSchedule(res);
    return res;
  } catch (e) {
    return rejectWithValue(e?.message || 'Request failed');
  }
});

// export const setControllersFields = createAsyncThunk(
//   'controller/setValues',
//   async (params, { getState }) => {
//     const state = getState();
//     const res = await saveControllersFields(
//       state.controllers.token,
//       params.id,
//       params.data,
//     );

//     // TODO: replace measurements with enum
//     return res;
//   },
// );

// export const fetchControllersFields = createAsyncThunk(
//   'controllers/fetchFields',
//   async (arg, { getState }) => {
//     const state = getState();
//     const res = await getAllDevicesFields(state.controllers.token);

//     // TODO: replace measurements with enum
//     return res;
//   },
// );

// export const fetchMultiCurrentData = createAsyncThunk(
//   'controllers/fetchMultiCurrentData',
//   async params => {
//     const res = await getLatestDataFromMultipleControllers(
//       params.auth,
//       params.measurements,
//       params.controllerIds,
//     );

//     return res;
//   },
// );

// export const fetchMultiData = createAsyncThunk(
//   'controllers/fetchMultiData',
//   async params => {
//     const res = await getControllerData(
//       params.auth,
//       params.measurements,
//       params.controllerIds,
//       params.timeRange,
//     );

//     return res;
//   },
// );

// export const fetchEvents = createAsyncThunk(
//   'controllers/fetchEvents',
//   async params => {
//     const res = await getEvents(
//       params.auth,
//       params.controllerId,
//       params.timeRange,
//       params.userTimeZone,
//     );

//     return res;
//   },
// );

// export const fetchMultiAlerts = createAsyncThunk(
//   'controllers/fetchMultiAlerts',
//   async params => {
//     const res = await getAlertsFromMultipleControllers(
//       params.auth,
//       params.controllerIds,
//       params.timeRange,
//     );

//     return res;
//   },
// );

const STORAGE_KEY = 'controllers_presets';

const safeReadPresets = () => {
  if (typeof window === 'undefined') return []; // SSR/Node guard
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(data)) return [];
    return data.map(p => p).filter(isValidPreset);
  } catch {
    return [];
  }
};

const isValidPreset = p =>
  p &&
  typeof p.id === 'string' &&
  typeof p.name === 'string' &&
  (p.power === 'on' || p.power === 'off') &&
  ['cool', 'heat', 'fan', 'auto'].includes(p.mode) &&
  typeof p.temp === 'number' &&
  ['low', 'med', 'high', 'auto'].includes(p.fan) &&
  typeof p.color === 'string' &&
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(p.color);

//#endregion
const DEFAULT_CAV_START_SCHEDULE = {
  0: '07:00:00',
  1: '07:00:00',
  2: '07:00:00',
  3: '07:00:00',
  4: '07:00:00',
  5: '07:00:00',
  6: '07:00:00',
};

const DEFAULT_CAV_END_SCHEDULE = {
  0: '22:00:00',
  1: '22:00:00',
  2: '22:00:00',
  3: '22:00:00',
  4: '22:00:00',
  5: '22:00:00',
  6: '22:00:00',
};

const parseCavSchedule = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      // fall through to single-time schedule
    }
    return { 0: trimmed, 1: trimmed, 2: trimmed, 3: trimmed, 4: trimmed, 5: trimmed, 6: trimmed };
  }
  return null;
};

const getInitialSelectedGraphsByDeviceType = deviceType =>
  String(deviceType || '')
    .toLowerCase()
    .includes('relay')
    ? ['channel_1', 'channel_2']
    : ['on_off'];

const isInitialControllerGraphSelection = selectedGraphs =>
  selectedGraphs.length === 0 ||
  (selectedGraphs.length === 1 && selectedGraphs[0] === 'on_off') ||
  (selectedGraphs.length === 2 && selectedGraphs.includes('channel_1') && selectedGraphs.includes('channel_2'));

const areGraphSelectionsEqual = (firstSelection, secondSelection) =>
  firstSelection.length === secondSelection.length && firstSelection.every((graph, index) => graph === secondSelection[index]);

const controllersInitialState = {
  controllers: [],
  filteredControllers: [],
  searchString: '',
  fields: {},
  fieldValues: {},
  names: {},
  selectedStatus: '1',
  selectedGraphs: ['on_off'],
  data: {
    key: '',
    rangeMeasurements: {},
    newData: 0,
    controllerName: '',
    hasRadar: false,
    building: '',
  },
  energyInfo: {
    hvacUnitOnCount: 0,
    coolWaterOpenCount: 0,
    kwhSum: 0,
    kwhWastePotentialSum: 0,
    totalRecordsInCAVTimeRange: 0,
    avgCoolWaterValveWhenActive: 0,
    cavStartTime: DEFAULT_CAV_START_SCHEDULE,
    cavEndTime: DEFAULT_CAV_END_SCHEDULE,
  },
  lastUpdate: 0,
  firstUpdate: 0,
  currentMeasurements: {},
  events: [],
  selectedControllers: [],
  timeRange: { label: 'Today', value: 86400000 },
  loading: false,
  chartPopUp: undefined,
  shouldUpdateControllerList: false,
  device_mac: undefined,
  selectedSinglePreset: undefined,
  hourlyPresetsArray: safeReadPresets() || [],
  schedulesIds: [],
  schedules: [],
  schedulesStatus: 'idle',
  schedulesError: null,
  activeScheduleId: undefined,
  selectedScheduleId: null,
  isScheduleModalopen: false,
};

export const controllersSlice = createSlice({
  name: 'controllers',
  initialState: controllersInitialState,
  reducers: {
    clearSchedulesData: state => {
      clearSchedules();
      // state.controllersInitialState = controllersInitialState;
      state.schedulesIds = [];
      state.schedules = [];
      state.schedulesStatus = 'idle';
      state.schedulesError = null;
      state.activeScheduleId = undefined;
      state.selectedScheduleId = null;
      state.isScheduleModalopen = false;
    },
    clear: state => {
      clearSchedules();
      state.controllers = [];
      state.filteredControllers = [];
      state.searchString = '';
      state.fields = {};
      state.fieldValues = {};
      state.names = {};
      state.selectedStatus = '1';
      state.selectedGraphs = ['on_off'];
      state.data = {
        key: '',
        rangeMeasurements: {},
        newData: 0,
        controllerName: '',
        hasRadar: false,
        building: '',
      };
      state.energyInfo = {
        hvacUnitOnCount: 0,
        coolWaterOpenCount: 0,
        kwhSum: 0,
        kwhWastePotentialSum: 0,
        totalRecordsInCAVTimeRange: 0,
        avgCoolWaterValveWhenActive: 0,
        cavStartTime: DEFAULT_CAV_START_SCHEDULE,
        cavEndTime: DEFAULT_CAV_END_SCHEDULE,
      };
      state.lastUpdate = 0;
      state.firstUpdate = 0;
      state.currentMeasurements = {};
      state.events = [];
      state.selectedControllers = [];
      state.timeRange = { label: 'Today', value: 86400000 };
      state.loading = false;
      state.chartPopUp = undefined;
      state.shouldUpdateControllerList = false;
      state.device_mac = undefined;
      state.selectedSinglePreset = undefined;
      state.hourlyPresetsArray = safeReadPresets() || [];
      state.schedulesIds = [];
      state.schedules = [];
      state.schedulesStatus = 'idle';
      state.schedulesError = null;
      state.activeScheduleId = undefined;
      state.selectedScheduleId = null;
      state.isScheduleModalopen = false;
    },
    clearControllerData: state => {
      state.data = {
        key: '',
        lastUpdate: 0,
        firstUpdate: 0,
        rangeMeasurements: {},
        newData: 0,
        controllerName: '',
        hasRadar: false,
        building: '',
      };
      state.timeRange = { label: 'Today', value: 86400000 };
      state.newData = 0;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setControllerTimeRangeValue: (state, action) => {
      const NewTimeRange = {
        label: action.payload.label,
        value: action.payload.value,
      };
      state.timeRange = NewTimeRange;
    },
    fetchControllerRangeData: (state, action) => {
      const NewTimeRange = {
        label: action.payload.label,
        value: action.payload.value,
      };
      state.timeRange = NewTimeRange;
    },
    setChartPopUp: (state, action) => {
      state.chartPopUp = action.payload;
    },
    toggleGraphs: (state, action) => {
      const graphNames = action.payload;
      state.selectedGraphs = graphNames;
    },
    setSearchString: (state, action) => {
      state.searchString = action.payload;
    },
    setControllerStatus: (state, action) => {
      state.selectedStatus = action.payload;
    },
    setOcpAvgsDatesObj: (state, action) => {
      state.ocpDatesObj = action.payload;
    },
    setSelectedControllers: (state, action) => {
      state.selectedControllers = action.payload;
    },
    updateFilteredControllers: (state, action) => {
      const filters = action.payload.controllerFilters;
      const controllers = JSON.parse(JSON.stringify(state.controllers));
      const currentUser = action.payload.currentUser;

      const filtered = filterDevices(controllers, filters, currentUser);
      state.filteredControllers = filtered;
    },
    addPreset: (state, action) => {
      // const preset = { ...action.payload };
      state.hourlyPresetsArray.push(action.payload);
      // persist after each change (simple approach)
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state.hourlyPresetsArray));
      }
    },
    deletePreset: (state, action) => {
      const presetId = action.payload;
      state.hourlyPresetsArray = state.hourlyPresetsArray.filter(p => p.id !== presetId);
      // persist after each change (simple approach)
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state.hourlyPresetsArray));
      }
    },
    setSelectedSinglePreset: (state, action) => {
      state.selectedSinglePreset = action.payload;
    },
    changeActiveScheduleId: (state, action) => {
      state.activeScheduleId = action.payload;
    },
    changeSelectedScheduleId: (state, action) => {
      state.selectedScheduleId = action.payload;
    },
    setScheduleModalopen: (state, action) => {
      state.isScheduleModalopen = action.payload;
    },
    scheduleDeleted: (state, action) => {
      const scheduleId = action.payload;
      // Remove from cache
      removeSchedule(scheduleId);
      // Remove from Redux array
      state.schedulesIds = state.schedulesIds.filter(id => id !== scheduleId);
    },
    scheduleUpdated: (state, action) => {
      const schedule = action.payload;
      // Update in cache
      putSchedule(schedule);
      // Add to Redux array if not already there
      if (!state.schedulesIds.includes(schedule.id)) {
        state.schedulesIds.push(schedule.id);
      }
    },
  },
  extraReducers: {
    // Add reducers for the async actions in the slice
    [fetchControllers.fulfilled]: (state, action) => {
      const controllers = action.payload.resControllers || [];

      // Sort controllers: active (status 1) first, then inactive (status 2)
      // const sortedControllers = controllers.sort((a, b) => {
      //   if (a.status === 1 && b.status === 2) return -1;
      //   if (a.status === 2 && b.status === 1) return 1;
      //   return 0; // Keep original order if same status
      // });

      state.controllers = controllers;
      state.names = controllers.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
      state.shouldUpdateControllerList = true;
      state.loading = false;
    },
    [fetchControllers.pending]: state => {
      state.shouldUpdateControllerList = false;
      state.loading = true;
    },
    [fetchControllers.rejected]: state => {
      state.loading = false;
    },
    [fetchInitialData.fulfilled]: (state, action) => {
      const newState = structuredClone(current(state.data));

      let index = state.data.findIndex(item => item.key === action.payload.id);

      try {
        if (index === -1) {
          index =
            newState.push({
              key: action.payload.id,
              log: {
                measurements: action.payload.data,
                lastUpdate: new Date(),
              },
            }) - 1;
        } else {
          newState[index].log = {
            measurements: action.payload.data,
            lastUpdate: new Date(),
          };
        }

        state.data = newState;
      } catch (err) {
        console.log(err);
      }
    },
    // [fetchMultiData.fulfilled]: (state, action) => {
    //   const newState = structuredClone(current(state.data));

    //   try {
    //     action.payload?.forEach(value => {
    //       let index = state.data.findIndex(item => item.key === value.id);

    //       if (index === -1) {
    //         index =
    //           newState.push({
    //             key: action.payload.id,
    //             log: {
    //               measurements: action.payload.data,
    //               lastUpdate: new Date(),
    //             },
    //           }) - 1;

    //         newMap[value.id] = index;
    //       } else {
    //         newState[index].log = {
    //           measurements: action.payload.data,
    //           lastUpdate: new Date(),
    //         };
    //       }
    //     });
    //     state.data = newState;
    //   } catch (err) {
    //     console.log(err);
    //   }
    // },

    [fetchControllerRangeData.pending]: (state, action) => {
      state.loading = true;
      state.data.rangeMeasurements = {};
    },
    [fetchControllerRangeData.rejected]: (state, action) => {
      state.data.rangeMeasurements = {};
      state.loading = false;
    },
    [fetchControllerRangeData.fulfilled]: (state, action, navigate) => {
      let newState_data = structuredClone(current(state.data));
      // let didMapChange = false;
      let controllerIdInStore;
      if (!action.payload) {
        return;
      }
      if (state.data.key === action.payload.id) {
        // first time fetching controller min max data
        controllerIdInStore = true;
      } else {
        controllerIdInStore = false;
      }
      try {
        newState_data = {
          key: action.payload.id,
          rangeMeasurements: action.payload.data.measurements,
          lastUpdate: action.payload.lastUpdate,
          firstUpdate: state.data.firstUpdate,
          newData: state.data.newData + 1,
          ocpAvgsData: [],
          controllerName: action.payload.data.sensorName,
          hasRadar: action.payload.data.hasRadar,
          building_id: action.payload.data.building_id,
          building: action.payload.data.building,
        };

        const newStateEnergyInfo = {
          hvacUnitOnCount: action.payload.data.onHoursCount,
          coolWaterOpenCount: action.payload.data.coolWaterValveOpenCount,
          kwhSum: action.payload.data.kwhSum,
          kwhWastePotentialSum: action.payload.data.kwhWastePotentialSum,
          totalRecordsInCAVTimeRange: Number(action.payload.data.totalRecordsInCAVTimeRange ?? 0),
          avgCoolWaterValveWhenActive: action.payload.data.avgCoolWaterValveWhenActive,
          cavStartTime: parseCavSchedule(action.payload.data.cav_start_time) || DEFAULT_CAV_START_SCHEDULE,
          cavEndTime: parseCavSchedule(action.payload.data.cav_end_time) || DEFAULT_CAV_END_SCHEDULE,
        };

        state.data = newState_data;
        state.energyInfo = newStateEnergyInfo;
        state.loading = false;
        // state.controlLoading = false;
        // state.newData++;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchCurrentData.pending]: (state, action) => {
      state.controlLoading = true;
    },
    [fetchCurrentData.fulfilled]: (state, action) => {
      try {
        state.currentMeasurements = action.payload.data.current;
        if (isInitialControllerGraphSelection(state.selectedGraphs)) {
          const initialSelectedGraphs = getInitialSelectedGraphsByDeviceType(action.payload.data.current?.device_type);
          if (!areGraphSelectionsEqual(state.selectedGraphs, initialSelectedGraphs)) {
            state.selectedGraphs = initialSelectedGraphs;
          }
        }
        state.lastUpdate = action.payload.data.lastUpdate;
        state.firstUpdate = action.payload.data.firstUpdate;
        state.device_mac = action.payload.data.current.device_mac;
        state.data.controllerName = action.payload.data.current.name;
        state.activeScheduleId = action.payload.data.current.active_schedule_id;
        state.selectedScheduleId = action.payload.data.current.active_schedule_id;

        state.controlLoading = false;
      } catch (err) {
        console.log(err);
      }
    },
    [fetchUserSchedules.pending]: (state, action) => {
      state.schedulesStatus = 'loading';
      state.schedulesError = null;
    },
    [fetchUserSchedules.rejected]: (state, action) => {
      state.schedulesStatus = 'failed';
      state.schedulesError = action.payload?.message || 'Failed to fetch';
    },
    [fetchUserSchedules.fulfilled]: (state, action) => {
      const schedules = action.payload;
      putManySchedules(schedules);
      state.schedulesIds = schedules.map(s => s.id);
      state.schedulesStatus = 'succeeded';
    },
    // Metadata only (lightweight for cards)
    [fetchSchedulesDetails.pending]: state => {
      state.schedulesStatus = 'loading';
      state.schedulesError = null;
    },
    [fetchSchedulesDetails.rejected]: (state, action) => {
      state.schedulesStatus = 'failed';
      state.schedulesError = action.payload?.message || 'Failed to fetch details';
    },
    [fetchSchedulesDetails.fulfilled]: (state, action) => {
      const schedulesMeta = action.payload;
      state.schedules = schedulesMeta; // Store lightweight metadata
      state.schedulesIds = schedulesMeta.map(s => s.schedule_id);
      state.schedulesStatus = 'succeeded';
    },
    [fetchControllerSchedules.pending]: (state, action) => {
      state.schedulesStatus = 'loading';
      state.schedulesError = null;
    },
    [fetchControllerSchedules.rejected]: (state, action) => {
      state.schedulesStatus = 'failed';
      state.schedulesError = action.payload?.message || 'Failed to fetch';
    },
    [fetchControllerSchedules.fulfilled]: (state, action) => {
      const schedules = action.payload.schedules;
      putManySchedules(schedules);
      state.schedulesIds = schedules.map(s => s.id);
      state.schedulesStatus = 'succeeded';
      state.selectedScheduleId = action.payload.active_schedule_id;
      state.activeScheduleId = action.payload.active_schedule_id;
    },
    [fetchSchedule.fulfilled]: (state, action) => {
      //  add ID if not already in array
      if (!state.schedulesIds.includes(action.payload.id)) {
        state.schedulesIds.push(action.payload.id);
      }
    },
    [deleteScheduleById.fulfilled]: (state, action) => {
      // Remove from Redux state immediately
      const scheduleId = action.meta.arg; // Get scheduleId from thunk argument
      removeSchedule(scheduleId);
      state.schedulesIds = state.schedulesIds.filter(id => id !== scheduleId);
    },
    [saveSchedule.fulfilled]: (state, action) => {
      const schedule = action.payload;
      if (schedule) {
        putSchedule(schedule);
        // Check for duplicates before pushing
        if (!state.schedulesIds.includes(schedule.id)) {
          state.schedulesIds.push(schedule.id);
        }
        state.selectedScheduleId = schedule.id;
        state.activeScheduleId = schedule.id;
      }
    },
    [activateScheduleForDevices.fulfilled]: (state, action) => {
      // Refresh the activated schedule from response
      if (action.payload?.schedule_id) {
        state.activeScheduleId = action.payload.schedule_id;
      }
    },
    [saveRecommendationForDevices.pending]: (state, action) => {
      state.schedulesStatus = 'loading';
      state.schedulesError = null;
    },
    [saveRecommendationForDevices.rejected]: (state, action) => {
      state.schedulesStatus = 'failed';
      state.schedulesError = action.payload || 'Failed to save recommendations';
    },
    [saveRecommendationForDevices.fulfilled]: (state, action) => {
      const schedules = action.payload?.schedules;
      if (schedules) {
        // Add new schedule IDs without duplicates
        schedules.forEach(schedule => {
          if (!state.schedulesIds.includes(schedule.id)) {
            state.schedulesIds.push(schedule.id);
          }
        });
      }
      state.schedulesStatus = 'succeeded';
    },
    [activateRecommendationsForDevices.pending]: state => {
      state.schedulesStatus = 'loading';
      state.schedulesError = null;
    },
    [activateRecommendationsForDevices.rejected]: (state, action) => {
      state.schedulesStatus = 'failed';
      state.schedulesError = action.payload || 'Failed to activate recommendations';
    },
    [activateRecommendationsForDevices.fulfilled]: (state, action) => {
      state.schedulesStatus = 'succeeded';
    },
  },
});

export const {
  clear,
  clearControllerData,
  clearSchedulesData,
  updateFilteredControllers,
  setControllerTimeRangeValue,
  setControllerStatus,
  setLoading: setControllerLoading,
  setChartPopUp: setControllerChartPopUp,
  toggleGraphs: toggleControllerGraphs,
  setSearchString: setControllerSearchString,
  setOcpAvgsDatesObj: setControllerOcpAvgsDatesObj,
  addPreset,
  deletePreset,
  setSelectedSinglePreset,
  setSelectedControllers,
  changeActiveScheduleId,
  changeSelectedScheduleId,
  setScheduleModalopen,
  scheduleDeleted,
  scheduleUpdated,
} = controllersSlice.actions;
